import * as React from "react";
import { useState, useEffect } from "react";
import {
  Title,
  Datagrid,
  TextField,
  DateField,
  useDataProvider,
  useNotify,
  ReferenceField,
  RecordContextProvider,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Divider,
  alpha,
  MenuItem,
  Select,
} from "@mui/material";
import {
  BarChart,
  PieChart,
  ChartsTooltip,
  ChartsAxis,
  PieArcLabel,
  BarElement,
} from "@mui/x-charts";
import {
  LocalShipping as ShippedIcon,
  Payment as PaidIcon,
  PendingActions as PendingIcon,
  BuildCircle as ProcessingIcon,
  CheckCircle as DeliveredIcon,
  Cancel as CancelledIcon,
  Person as BuyerIcon,
  Store as SellerIcon,
  Category as TypeIcon,
  Timeline as TimelineIcon,
  AttachMoney as RevenueIcon,
} from "@mui/icons-material";
import { ExportListAction } from "./UsersReport";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";
import { OrderList } from "../Orders";
import UserDialog from "../components/user-details";

// Format SDG currency with K/M abbreviations
export const formatSDG = (amount) => {
  if (!amount) return "0 SDG";

  amount = Number(amount);
  if (isNaN(amount)) return "0 SDG";

  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M SDG`;
  }
  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K SDG`;
  }
  return `${amount.toLocaleString()} SDG`;
};

// Status icon component with modern styling
const StatusIcon = ({ status }) => {
  const iconProps = {
    fontSize: "small",
    sx: {
      mr: 1,
      borderRadius: "50%",
      padding: "4px",
      backgroundColor: alpha("#fff", 0.2),
    },
  };
  switch (status) {
    case "Pending":
      return <PendingIcon color="warning" {...iconProps} />;
    case "Processing":
      return <ProcessingIcon color="info" {...iconProps} />;
    case "Paid":
      return <PaidIcon color="success" {...iconProps} />;
    case "Shipped":
      return <ShippedIcon color="secondary" {...iconProps} />;
    case "Delivered":
      return <DeliveredIcon color="success" {...iconProps} />;
    case "Cancelled":
      return <CancelledIcon color="error" {...iconProps} />;
    default:
      return <PendingIcon color="action" {...iconProps} />;
  }
};

// Modern Status Field component
const StatusField = ({ record }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: "12px",
      backgroundColor: alpha("#fff", 0.1),
      width: "fit-content",
    }}
  >
    <StatusIcon status={record?.status} />
    <Typography variant="body2" fontWeight="500">
      {record?.status}
    </Typography>
  </Box>
);

// Modern Summary Card Component
export const SummaryCard = ({ title, value, icon: Icon, secondaryValue }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        transition: "all 0.3s ease",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.light,
          0.1
        )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
        backdropFilter: "blur(8px)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 25px 0 rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "24px !important",
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: "12px",
            width: 60,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            color: theme.palette.primary.contrastText,
            boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
          }}
        >
          <Icon fontSize="medium" />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            gutterBottom
            sx={{ opacity: 0.8 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            component="div"
            fontWeight="800"
            sx={{
              background: `linear-gradient(135deg, ${
                theme.palette.text.primary
              } 0%, ${alpha(theme.palette.text.primary, 0.8)} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {value}
          </Typography>
          {secondaryValue && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ opacity: 0.7 }}
            >
              {secondaryValue}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Modern Pie Chart Component
// export const SafePieChart = ({ data, title, height = 300 }) => {
//   const theme = useTheme();

//   if (!data || data.length === 0) {
//     return (
//       <Card sx={{
//         background: alpha(theme.palette.background.paper, 0.8),
//         backdropFilter: 'blur(8px)'
//       }}>
//         <CardContent>
//           <Typography variant="h6" gutterBottom fontWeight="bold">
//             {title}
//           </Typography>
//           <Alert severity="info">No data available for this chart</Alert>
//         </CardContent>
//       </Card>
//     );
//   }

//   const chartData = data.map(item => ({
//     id: item.status || item.type || item.name || 'unknown',
//     label: item.status || item.type || item.name || 'Unknown',
//     value: parseInt(item.count || item.totalRevenue || 0),
//     color: {
//       'Pending': theme.palette.warning.main,
//       'Processing': theme.palette.info.main,
//       'Paid': theme.palette.success.main,
//       'Shipped': theme.palette.secondary.main,
//       'Delivered': theme.palette.success.dark,
//       'Cancelled': theme.palette.error.main,
//       'Crop': theme.palette.primary.main,
//       'Input': theme.palette.secondary.main,
//       'pending': theme.palette.warning.main,
//       'accepted': theme.palette.success.main,
//       'rejected': theme.palette.error.main,

//       'Crop': theme.palette.primary.main,
//       'Input': theme.palette.secondary.main,
//       'Service': theme.palette.info.main,

//       'نقل': theme.palette.primary.light,
//       'تخليص جمركي': theme.palette.info.dark,
//       'تخزين': theme.palette.secondary.light,
//       'عمالة': theme.palette.warning.light,

//       // Default fallbacks
//       'unknown': theme.palette.grey[500]
//     }[item.status || item.type] || theme.palette.primary.main
//   }));

//   return (
//     <Card sx={{
//       background: alpha(theme.palette.background.paper, 0.8),
//       backdropFilter: 'blur(8px)',
//       height: '100%'
//     }}>
//       <CardContent>
//         <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
//           {title}
//         </Typography>
//         <Box sx={{ height }}>
//           <PieChart
//             series={[{
//               data: chartData,
//               innerRadius: 40,
//               outerRadius: 100,
//               paddingAngle: 2,
//               cornerRadius: 6,
//               highlightScope: { faded: 'global', highlighted: 'item' },
//               faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
//               arcLabel: (item) => `${item.label} (${item.value})`,
//               arcLabelMinAngle: 20,
//             }]}
//             slotProps={{
//               legend: {
//                 direction: 'row',
//                 position: { vertical: 'bottom', horizontal: 'middle' },
//                 padding: 0,
//                 labelStyle: {
//                   fontSize: 12,
//                   fill: theme.palette.text.primary,
//                 },
//               },
//             }}
//           >
//             <ChartsTooltip />
//             <PieArcLabel />
//           </PieChart>
//         </Box>
//       </CardContent>
//     </Card>
//   );
// };
export const SafePieChart = ({ data, title, height = 300 }) => {
  const theme = useTheme();

  // Define color palette to cycle through for unknown items
  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.info.light,
  ];

  // Predefined color mappings
  const colorMap = {
    // Input types (English)
    Crop: theme.palette.primary.main,
    Input: theme.palette.secondary.main,
    Service: theme.palette.info.main,

    // Statuses
    Pending: theme.palette.warning.main,
    Processing: theme.palette.info.main,
    Paid: theme.palette.success.main,
    Shipped: theme.palette.secondary.main,
    Delivered: theme.palette.success.dark,
    Cancelled: theme.palette.error.main,
    pending: theme.palette.warning.main,
    accepted: theme.palette.success.main,
    rejected: theme.palette.error.main,

    // Arabic service types
    نقل: theme.palette.primary.light,
    "تخليص جمركي": theme.palette.info.dark,
    تخزين: theme.palette.secondary.light,
    عمالة: theme.palette.warning.light,
  };

  if (!data || data.length === 0) {
    return (
      <Card
        sx={{
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(8px)",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {title}
          </Typography>
          <Alert severity="info">No data available for this chart</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item, index) => {
    const category = item.status || item.type || item.name || "unknown";
    return {
      id: category,
      label: category,
      value: parseInt(item.count || item.totalRevenue || 0),
      // Use mapped color if exists, otherwise use palette based on index
      color: colorPalette[index % colorPalette.length],
    };
  });

  return (
    <Card
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(8px)",
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ height }}>
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius: 40,
                outerRadius: 100,
                paddingAngle: 2,
                cornerRadius: 6,
                highlightScope: { faded: "global", highlighted: "item" },
                faded: {
                  innerRadius: 30,
                  additionalRadius: -30,
                  color: "gray",
                },
                arcLabel: (item) => `${item.label} (${item.value})`,
                arcLabelMinAngle: 20,
              },
            ]}
            colors={chartData.map((item) => item.color)}
            margin={{ bottom: 100 }}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 10,
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.primary,
                },
              },
            }}
          >
            <ChartsTooltip />
            <PieArcLabel />
          </PieChart>
        </Box>
      </CardContent>
    </Card>
  );
};

// Modern Bar Chart Component
export const SafeBarChart = ({
  data,
  title,
  xKey = "month",
  yKey = "count",
  height = 300,
}) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Card
        sx={{
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(8px)",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {title}
          </Typography>
          <Alert severity="info">No data available for this chart</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    [xKey]: `${item.month || item.type} ${item.year || ""}`.trim(),
    [yKey]: parseInt(item[yKey] || 0),
  }));

  return (
    <Card
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(8px)",
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ height }}>
          <BarChart
            dataset={chartData}
            xAxis={[
              {
                scaleType: "band",
                dataKey: xKey,
                label: xKey === "month" ? "Month" : "Type",
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.primary,
                },
                tickLabelStyle: {
                  fontSize: 10,
                  fill: theme.palette.text.secondary,
                },
              },
            ]}
            series={[
              {
                dataKey: yKey,
                label: yKey === "count" ? "Count" : "Revenue",
                color: theme.palette.primary.main,
                highlightScope: {
                  highlighted: "item",
                  faded: "global",
                },
              },
            ]}
            yAxis={[
              {
                label: yKey === "count" ? "Number of Orders" : "Revenue (SDG)",
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.primary,
                },
              },
            ]}
            colors={[theme.palette.primary.main]}
            grid={{ vertical: true }}
          >
            <ChartsAxis />
            <ChartsTooltip
              formatter={(value) =>
                yKey === "count" ? value : formatSDG(value)
              }
            />
            <BarElement
              style={{
                rx: 4,
                ry: 4,
              }}
            />
          </BarChart>
        </Box>
      </CardContent>
    </Card>
  );
};

// Modern Top Performers List Component
export const TopPerformersList = ({ title, data, icon: Icon }) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Alert severity="info">No {title.toLowerCase()} data available</Alert>
    );
  }

  return (
    <Card
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(8px)",
        height: "100%",
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="bold"
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Icon
            sx={{
              mr: 1,
              background: alpha(theme.palette.primary.main, 0.1),
              padding: "6px",
              borderRadius: "50%",
              color: theme.palette.primary.main,
            }}
          />
          {title}
        </Typography>

        <List sx={{ py: 0 }}>
          {data.map((item, index) => (
            <React.Fragment key={item.id}>
              <RecordContextProvider value={item.user}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 1,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: "8px",
                    },
                  }}
                >
                  {/* This will render person.name from record = item.user */}
                  <UserDialog source="user" />
                </ListItem>
              </RecordContextProvider>
              <ListItem
                sx={{
                  px: 0,
                  py: 1,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: "8px",
                  },
                }}
              >
                <ListItemText
                  secondary={
                    <Typography variant="body2" color="textSecondary">
                      {item.orderCount} orders
                    </Typography>
                  }
                  sx={{ my: 0 }}
                />
                <Box
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                  }}
                >
                  #{index + 1}
                </Box>
              </ListItem>
              {index < data.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

const OrderReports = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [buyerType, setBuyerType] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [buyerState, setBuyerState] = useState("");
  const [sellerState, setSellerState] = useState("");
  const [states, setStates] = useState([]);
  const [startDate, setStartDate] = useState(""); // YYYY‑MM‑DD
  const [endDate, setEndDate] = useState("");

  const allStatuses = [
    "Processing",
    "Delivered",
    "Cancelled",
    "Pending",
    "Paid",
    "Shipped",
  ];
  const types = [
    { id: "farmer", name: "Farmer" },
    { id: "agriculture_cooperative", name: "Farmer Cooperative" },
    { id: "consumer_cooperative", name: " Consumer Cooperative" },
    { id: "supplier", name: "Supplier" },
    { id: "buyer", name: "Buyer" },
    { id: "marketer", name: "Marketer" },
  ];
  const negotiationTypes = ["Crop", "Input", "Service"]; // adjust to your types
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const params = new URLSearchParams();

        const stateResponse = await fetch(
          `${process.env.REACT_APP_URL}/admin/state`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const statesData = await stateResponse.json();
        setStates(statesData);

        if (statusFilter) params.append("status", statusFilter);
        if (typeFilter) params.append("type", typeFilter);
        if (buyerType) params.append("buyerType", buyerType);
        if (buyerState) params.append("buyerState", buyerState);
        if (sellerType) params.append("sellerType", sellerType);
        if (sellerState) params.append("sellerState", sellerState);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        const url =
          `${process.env.REACT_APP_URL}/admin/reports/orders` +
          (params.toString() ? `?${params.toString()}` : "");
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        const data = responseData.data;

        const transformedData = {
          summary: data[0]?.summary || { totalOrders: 0 },
          statusDistribution: data[1]?.statusDistribution || [],
          timeSeries: data[1]?.timeSeries || [],
          topBuyers: data[2]?.topBuyers || [],
          topSellers: data[2]?.topSellers || [],
          orders: Array.isArray(data[3]) ? data[3] : [],
          revenueByType: data[4]?.revenueByType || [],
        };

        setReportData(transformedData);
      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err);
        notify("Error loading order report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };
    // setLoading(true);
    fetchReportData();
  }, [
    statusFilter,
    typeFilter,
    buyerState,
    buyerType,
    sellerType,
    sellerState,
    startDate,
    endDate,
  ]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load order report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error.message}
        </Box>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No order report data available.
      </Alert>
    );
  }

  const {
    summary,
    statusDistribution,
    timeSeries,
    topBuyers,
    topSellers,
    orders,
    revenueByType,
  } = reportData;

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title="Order Reports" />
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Order Analytics Dashboard
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total Orders"
            value={summary.totalOrders}
            icon={TimelineIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Revenue (Crops)"
            value={formatSDG(
              revenueByType.find((r) => r.type === "Crop")?.totalRevenue || 0
            )}
            icon={RevenueIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Revenue (Inputs)"
            value={formatSDG(
              revenueByType.find((r) => r.type === "Input")?.totalRevenue || 0
            )}
            icon={RevenueIcon}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Order List" />
          </Tabs>
        </Paper>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Status</Typography>
              <Select
                fullWidth
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {allStatuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Negotiation Type</Typography>
              <Select
                fullWidth
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {negotiationTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Buyer Type</Typography>
              <Select
                fullWidth
                value={buyerType}
                onChange={(e) => setBuyerType(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Any</MenuItem>
                {types.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Seller Type</Typography>
              <Select
                fullWidth
                value={sellerType}
                onChange={(e) => setSellerType(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Any</MenuItem>
                {types.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Buyer State</Typography>
              <Select
                fullWidth
                value={buyerState}
                onChange={(e) => setBuyerState(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Seller State</Typography>
              <Select
                fullWidth
                value={sellerState}
                onChange={(e) => setSellerState(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Any</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DatePicker
                label="From"
                value={startDate || null}
                onChange={(newDate) => {
                  // normalize to YYYY-MM-DD string or empty
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setStartDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <DatePicker
                label="To"
                value={endDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setEndDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SafePieChart
                data={statusDistribution}
                title="Order Status Distribution"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SafePieChart data={revenueByType} title="Revenue by Type" />
            </Grid>

            <Grid item xs={12} md={6}>
              <SafeBarChart
                data={timeSeries}
                title="Monthly Order Trends"
                xKey="month"
                yKey="count"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SafeBarChart
                data={revenueByType}
                title="Revenue by Type"
                xKey="type"
                yKey="totalRevenue"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TopPerformersList
                title="Top Buyers"
                data={topBuyers}
                icon={BuyerIcon}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TopPerformersList
                title="Top Sellers"
                data={topSellers}
                icon={SellerIcon}
              />
            </Grid>
          </Grid>
        </>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* <Grid item xs={12}>
      <List
        resource="orders"
        actions={<ExportListAction />}
        // Add these props for debugging:
        sx={{ border: '1px dashed red' }} // Visual debug
        empty={
          <Alert severity="info">
            No orders found. Check API response structure.
          </Alert>
        }
      >
        <Datagrid>
          <TextField source="id" />
          <ReferenceField 
            source="buyerId" 
            reference="users" 
            label="Buyer"
            link="show" // Makes buyer name clickable
          >
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField 
            source="buyerId" 
            reference="users" 
            label="Phone"
          >
            <TextField source="phonenumber" />
          </ReferenceField>
          <TextField source="type" label="Negotiation Type" />
          <TextField source="tradeType" label="Trade Type" />
          <DateField source="createdAt" label="Date" />
        </Datagrid>
      </List>
    </Grid> */}
          <OrderList resource="orders" />
        </Grid>
      )}
    </Box>
  );
};

export default OrderReports;
